import { useState, useMemo } from "react";

const useEmiCalculator = () => {

    const [loanAmount, setLoanAmount] = useState(500000);
    const [rate, setRate] = useState(8);
    const [tenure, setTenure] = useState(5);

    const { emi, totalAmount, totalInterest } = useMemo(() => {

        if (loanAmount <= 0 || rate <= 0 || tenure <= 0) {
            return {
                emi: 0,
                totalAmount: 0,
                totalInterest: 0
            };
        }

        const monthlyRate = rate / 12 / 100;
        const totalMonths = tenure * 12;

        const emiValue =
            (loanAmount *
                monthlyRate *
                Math.pow(1 + monthlyRate, totalMonths)) /
            (Math.pow(1 + monthlyRate, totalMonths) - 1);

        const totalPayment = emiValue * totalMonths;

        return {
            emi: emiValue,
            totalAmount: totalPayment,
            totalInterest: totalPayment - loanAmount
        };

    }, [loanAmount, rate, tenure]);

    return {
        title: "EMI Calculator",

        inputs: [
            {
                label: "Loan Amount",
                value: loanAmount,
                setter: setLoanAmount,
                min: 50000,
                max: 50000000,
                step: 50000,
            },
            {
                label: "Rate of Interest (p.a)",
                value: rate,
                setter: setRate,
                min: 1,
                max: 20,
                step: 0.1,
            },
            {
                label: "Loan Tenure (Years)",
                value: tenure,
                setter: setTenure,
                min: 1,
                max: 30,
                step: 1,
            },
        ],

        // Result Section
        monthlyEmi: emi,
        principalAmount: loanAmount,
        totalInterest,
        totalAmount,

        infoContent: {
            heading: "EMI Calculator – Loan EMI Calculation",
            description:
                "An EMI Calculator helps you calculate your monthly loan installment along with total interest payable and total repayment amount.",

            subHeading1: "What is an EMI Calculator?",
            subContent1:
                "It estimates your Equated Monthly Installment (EMI) based on loan amount, interest rate, and tenure.",

            subHeading2: "How It Works",
            subContent2:
                "It uses a standard EMI formula to calculate fixed monthly payments including both principal and interest components.",

            formula:
                "EMI = P × r × (1 + r)^n / ((1 + r)^n − 1)",

            subHeading3: "Benefits",
            benefits: [
                "Helps plan monthly budget",
                "Shows total interest payable",
                "Assists in choosing ideal tenure",
                "Supports better loan decisions",
            ],
        }
    };
};

export default useEmiCalculator;